import React, { useEffect, useMemo, useState } from 'react';
import { SpaceBetween, Box, Button, ColumnLayout, Icon, IconProps, Spinner } from '@amzn/awsui-components-core';
import { useFinTechContext } from 'src/components/context/FintechContextProvider';
import { HuddleStatsService } from '../services/HuddleStatsService';
import './AttendeePathPicker.scss';

export type AttendeePath = 'upload' | 'clone' | 'manual';

interface AttendeePathPickerProps {
  onSelect: (path: AttendeePath) => void;
  disabledPaths?: Set<AttendeePath>;
  displayName: string;
}

interface AttendeeOption {
  path: AttendeePath;
  icon: IconProps['name'];
  title: string;
  description: string;
  actionLabel: string;
}

interface HuddleStat {
  id: string;
  emoji?: string;
  value: string | null;
  label: string;
}

const ATTENDEE_OPTIONS: readonly AttendeeOption[] = [
  {
    path: 'upload',
    icon: 'upload',
    title: 'Smart Upload',
    description: 'Drop any file and AI will extract attendees automatically.',
    actionLabel: 'Upload & Go'
  },
  {
    path: 'clone',
    icon: 'copy',
    title: 'Clone a Meeting',
    description: 'Pick a past meeting or paste a link to import attendees.',
    actionLabel: 'Browse & Import'
  },
  {
    path: 'manual',
    icon: 'add-plus',
    title: 'Add Manually',
    description: 'Search and add attendees one at a time.',
    actionLabel: 'Start Fresh'
  }
];

const ALL_APPLICATIONS = 'All Applications';

const METRICS_CACHE_KEY = 'huddle_user_session_metrics';
const METRICS_CACHE_TTL_MS = 15 * 60 * 1000;

interface CachedMetrics {
  users: string;
  sessions: string;
  expiry: number;
}

function getCachedMetrics(): CachedMetrics | null {
  try {
    const raw = sessionStorage.getItem(METRICS_CACHE_KEY);
    if (!raw) return null;
    const cached: CachedMetrics = JSON.parse(raw);
    return Date.now() < cached.expiry ? cached : null;
  } catch {
    return null;
  }
}

function setCachedMetrics(users: string, sessions: string): void {
  try {
    sessionStorage.setItem(METRICS_CACHE_KEY, JSON.stringify({ users, sessions, expiry: Date.now() + METRICS_CACHE_TTL_MS }));
  } catch {
    // ignore
  }
}

function useHuddleStats(): { stats: HuddleStat[] } {
  const finOpsContext = useFinTechContext();
  const status = finOpsContext.finTechOpsUserMetricsDataStatus;
  const data = finOpsContext.userMetricsWithUserData?.data;

  const [meetingsScheduled, setMeetingsScheduled] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    HuddleStatsService.getMeetingsScheduledCount().then((count) => {
      if (!cancelled) setMeetingsScheduled(count);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const { users, sessions, metricsLoading } = useMemo(() => {
    const fmt = (n: number) => n.toLocaleString();
    const metricsReady = status === 'finished' && data?.length;
    const loading = !metricsReady && status === 'loading';
    const fallback = loading ? getCachedMetrics() : null;

    let computedUsers = fallback?.users ?? '—';
    let computedSessions = fallback?.sessions ?? '—';

    if (metricsReady) {
      const huddleData = data.filter((item) => item.application?.toLowerCase().includes('huddle') && item.application !== ALL_APPLICATIONS);
      computedUsers = fmt(new Set(huddleData.map((item) => item.user_login)).size);
      computedSessions = fmt(huddleData.reduce((sum, item) => sum + (item.measure_value || 0), 0));
    }

    return { users: computedUsers, sessions: computedSessions, metricsLoading: loading && !fallback };
  }, [status, data]);

  useEffect(() => {
    if (users !== '—' && sessions !== '—') {
      setCachedMetrics(users, sessions);
    }
  }, [users, sessions]);

  return useMemo(() => {
    const fmt = (n: number) => n.toLocaleString();
    return {
      stats: [
        { id: 'users', emoji: '👥', value: metricsLoading ? null : users, label: 'Users' },
        { id: 'sessions', emoji: '📊', value: metricsLoading ? null : sessions, label: 'Sessions' },
        { id: 'setup-time', emoji: '🚀', value: '~12s', label: 'Avg Setup Time' },
        { id: 'meetings', emoji: '📅', value: meetingsScheduled !== null ? fmt(meetingsScheduled) : null, label: 'Meetings Scheduled' }
      ]
    };
  }, [users, sessions, metricsLoading, meetingsScheduled]);
}

function LandingHeader() {
  return (
    <Box padding={{ horizontal: 'xxl', top: 'xxxl', bottom: 's' }} textAlign="center">
      <SpaceBetween alignItems="center" size="xxs">
        <span className="huddle-landing-badge">⚡ AI-Powered Scheduling</span>
        <Box fontSize="display-l" fontWeight="heavy" variant="h1">
          Schedule Meetings 10x Faster
        </Box>
        <Box color="text-body-secondary" fontSize="body-m" variant="p">
          Stop playing email tag. Huddle+ finds the perfect time, and builds the invite for you effortlessly.
        </Box>
      </SpaceBetween>
    </Box>
  );
}

function AttendeeOptionsSection({
  displayName,
  onSelect,
  disabledPaths
}: {
  displayName: string;
  onSelect: (path: AttendeePath) => void;
  disabledPaths?: Set<AttendeePath>;
}) {
  return (
    <Box padding={{ bottom: 'xl', horizontal: 'xxl' }} textAlign="center">
      <SpaceBetween size="m">
        <SpaceBetween alignItems="center" size="xxxs">
          <Box fontSize="heading-m" fontWeight="bold" textAlign="center" variant="h3">
            👋 Hey {displayName}, let's set up the meeting for you.
          </Box>
          <Box color="text-body-secondary" fontSize="body-m" textAlign="center" variant="p">
            How would you like to add attendees?
          </Box>
        </SpaceBetween>
        <div className="attendee-options-grid">
          {ATTENDEE_OPTIONS.map((option) => {
            const isDisabled = disabledPaths?.has(option.path) ?? false;
            return (
              <button
                key={option.path}
                type="button"
                className={`path-card${isDisabled ? ' path-card--disabled' : ''}`}
                onClick={() => onSelect(option.path)}
                disabled={isDisabled}
              >
                <SpaceBetween size="s" direction="vertical" alignItems="center">
                  <div className="path-card__icon">
                    <Icon name={option.icon} size="big" />
                  </div>
                  <Box fontSize="heading-l" textAlign="center" variant="strong">
                    {option.title}
                  </Box>
                  <Box color="text-body-secondary" textAlign="center" variant="p">
                    {option.description}
                  </Box>
                  <Button variant="primary" disabled={isDisabled}>
                    {option.actionLabel}
                  </Button>
                </SpaceBetween>
              </button>
            );
          })}
        </div>
      </SpaceBetween>
    </Box>
  );
}

// Real-time stats for Huddle+ from user metrics data
function StatsSection() {
  const { stats } = useHuddleStats();

  return (
    <Box padding={{ bottom: 'xl', horizontal: 'xxl' }}>
      <ColumnLayout borders="vertical" columns={4} variant="text-grid">
        {stats.map((stat) => (
          <SpaceBetween key={stat.id} alignItems="center" size="xxxs">
            {stat.emoji && (
              <Box fontSize="heading-xl" textAlign="center" variant="p">
                {stat.emoji}
              </Box>
            )}
            <Box fontWeight="heavy" variant="awsui-key-label" fontSize="heading-xl">
              {stat.value === null ? <Spinner /> : stat.value}
            </Box>
            <Box color="text-body-secondary" variant="small">
              {stat.label}
            </Box>
          </SpaceBetween>
        ))}
      </ColumnLayout>
    </Box>
  );
}

export const AttendeePathPicker: React.FC<AttendeePathPickerProps> = ({ onSelect, disabledPaths, displayName }) => (
  <SpaceBetween size="xl">
    <div>
      <SpaceBetween size="xs">
        <LandingHeader />
        <AttendeeOptionsSection displayName={displayName} onSelect={onSelect} disabledPaths={disabledPaths} />
        <StatsSection />
      </SpaceBetween>
    </div>
  </SpaceBetween>
);
